import axios from 'axios';
import {message} from 'antd';

import store from '../store';
import {history} from '../helpers/history';
import {LOGOUT, TOKEN_REFRESHED} from '../actions/types';

const AUTH_API_URL = process.env.REACT_APP_API_AUTH_URL || '/api/auth/';
const refreshClient = axios.create ();

let refreshPromise = null;
let isEndingSession = false;

const getStoredUser = () => {
  try {
    return JSON.parse (localStorage.getItem ('user'));
  } catch (error) {
    return null;
  }
};

const isProtectedRequest = config => {
  const headers = config && config.headers;

  return Boolean (
    headers &&
      (headers['x-access-token'] ||
        (headers.common && headers.common['x-access-token']))
  );
};

const isAuthenticationFailure = (response, config) => {
  const responseMessage =
    response && response.data && typeof response.data.message === 'string'
      ? response.data.message
      : '';

  return (
    response &&
    response.status === 401 &&
    (isProtectedRequest (config) || responseMessage.startsWith ('Unauthorized!'))
  );
};

const saveRefreshedSession = tokens => {
  const currentUser = getStoredUser ();

  if (!currentUser || !tokens.accessToken) {
    throw new Error ('Unable to restore the current session.');
  }

  const refreshedUser = {
    ...currentUser,
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken || currentUser.refreshToken,
  };

  localStorage.setItem ('user', JSON.stringify (refreshedUser));
  store.dispatch ({
    type: TOKEN_REFRESHED,
    payload: {user: refreshedUser},
  });

  return refreshedUser.accessToken;
};

const refreshAccessToken = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentUser = getStoredUser ();

  if (!currentUser || !currentUser.refreshToken) {
    return Promise.reject (new Error ('No refresh token is available.'));
  }

  refreshPromise = refreshClient
    .post (AUTH_API_URL + 'refreshtoken', {
      refreshToken: currentUser.refreshToken,
    })
    .then (response => saveRefreshedSession (response.data))
    .finally (() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

const endSession = () => {
  if (isEndingSession) {
    return;
  }

  isEndingSession = true;
  localStorage.removeItem ('user');
  store.dispatch ({type: LOGOUT});

  message.destroy ();

  if (history.location.pathname !== '/auth/signin') {
    history.replace ('/auth/signin', {
      from: history.location.pathname,
      sessionExpired: true,
    });
  }

  window.setTimeout (() => {
    message.destroy ();
    message.warning ('Your session has expired. Please sign in again.');
  }, 0);

  window.setTimeout (() => {
    isEndingSession = false;
  }, 1000);
};

const interceptorId = axios.interceptors.response.use (
  response => response,
  async error => {
    const response = error && error.response;
    const originalRequest = error && error.config;

    if (
      !response ||
      !originalRequest ||
      !isAuthenticationFailure (response, originalRequest)
    ) {
      return Promise.reject (error);
    }

    if (originalRequest._sessionRetry) {
      endSession ();
      return Promise.reject (error);
    }

    originalRequest._sessionRetry = true;

    try {
      const accessToken = await refreshAccessToken ();
      originalRequest.headers = originalRequest.headers || {};
      originalRequest.headers['x-access-token'] = accessToken;

      return axios (originalRequest);
    } catch (refreshError) {
      endSession ();
      return Promise.reject (refreshError);
    }
  }
);

export const removeSessionInterceptor = () => {
  axios.interceptors.response.eject (interceptorId);
};
